import { PERMISSION_ORDER } from "app/constants/permissionOrderConstants";
import { UserRoleModel } from "app/services/api/types/authorisation/models/UserRoleModel";
import { GroupedPermissionModel } from "app/services/api/types/permissions/models/GroupedPermissionModel";
import { useMemo } from "react";

function sortPermissions(permissions: UserRoleModel[]) {
  return [...permissions].sort((a, b) => {
    const indexA = PERMISSION_ORDER.findIndex((prefix) =>
      a.name.startsWith(prefix),
    );
    const indexB = PERMISSION_ORDER.findIndex((prefix) =>
      b.name.startsWith(prefix),
    );
    if (indexA === -1 && indexB === -1) {
      return a.name.localeCompare(b.name);
    }
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
}

function sortPermissionGroups(groups: GroupedPermissionModel[]) {
  return groups.map((group) => ({
    ...group,
    permissions: sortPermissions(group.permissions),
  }));
}

function enforceViewSelection(
  selectedIds: number[],
  permissions: UserRoleModel[],
) {
  const viewPermission = permissions.find((p) => p.name.startsWith("view"));
  if (!viewPermission) return selectedIds;

  const viewId = viewPermission.id;
  const requiresView = permissions.some(
    (p) =>
      ["create", "update", "delete"].some((prefix) =>
        p.name.startsWith(prefix),
      ) && selectedIds.includes(p.id),
  );

  if (requiresView && !selectedIds.includes(viewId)) {
    return [...selectedIds, viewId];
  }

  return selectedIds;
}

export function useSortedPermissionsWithEnforcement(
  permissionsData?: GroupedPermissionModel[],
) {
  const sortedPermissionsData = useMemo(() => {
    if (!permissionsData) return [];
    return sortPermissionGroups(permissionsData);
  }, [permissionsData]);

  const handleCheckboxSelection = (
    checked: boolean,
    permissionId: number,
    currentSelectedIds: number[],
    allPermissions: UserRoleModel[],
  ) => {
    let newSelected = checked
      ? [...currentSelectedIds, permissionId]
      : currentSelectedIds.filter((id) => id !== permissionId);

    newSelected = enforceViewSelection(newSelected, allPermissions);
    return newSelected;
  };

  return { sortedPermissionsData, handleCheckboxSelection };
}
